import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n";
import { SEO_PAGES } from "../seoPages";
import { scrollToId } from "../components/Navbar";
import { JsonLd } from "../components/Seo";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

export default function SeoPage({ slug }) {
  const { lang, L } = useLang();
  const page = SEO_PAGES[slug];
  if (!page) return null;

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faq.map((f) => ({
      "@type": "Question",
      name: L(f.q),
      acceptedAnswer: { "@type": "Answer", text: L(f.a) },
    })),
  };

  return (
    <div data-testid={`seo-page-${slug}`}>
      <JsonLd id={`faq-${slug}`} data={faqLd} />

      {/* HERO */}
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-20 lg:pt-28">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="max-w-3xl">
          <p className="mono-tech mb-8 text-[#A8853B]" data-testid="seo-eyebrow">{L(page.eyebrow)}</p>
          <h1 className="font-display text-4xl leading-[1.08] tracking-tight text-[#0E3B33] sm:text-5xl lg:text-6xl" data-testid="seo-h1">
            {L(page.h1)}
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#4A5248] sm:text-lg" data-testid="seo-lead">
            {L(page.lead)}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" data-testid="seo-cta-contact" className="ember-btn">
              {L({ en: "Start a Project", it: "Inizia un Progetto" })} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/" onClick={() => setTimeout(() => scrollToId("#calculator"), 400)} data-testid="seo-cta-estimate" className="ghost-btn">
              {L({ en: "Try the live estimate", it: "Prova il preventivo live" })}
            </Link>
          </div>
        </motion.div>
      </section>

      {/* BODY SECTIONS */}
      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="max-w-3xl space-y-20">
          {page.sections.map((s, i) => (
            <motion.div key={i} {...fadeUp} data-testid={`seo-section-${slug}-${i}`}>
              <div className="mono-tech mb-5 flex items-center gap-3 text-[#8A8F82]">
                <span className="text-[#A8853B]">{String(i + 1).padStart(2, "0")}</span>
                <span className="h-px flex-1 bg-[#0E3B33]/10" />
              </div>
              <h2 className="font-display text-3xl tracking-tight text-[#0E3B33] sm:text-4xl">{L(s.h2)}</h2>
              {(s.paras || []).map((p, j) => (
                <p key={j} className="mt-5 text-base leading-relaxed text-[#4A5248]">{L(p)}</p>
              ))}
              {s.list && (
                <ul className="mt-7 space-y-2.5">
                  {s.list.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-[#1E2B26]" data-testid={`seo-list-${slug}-${i}-${j}`}>
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[#C9A84C]" />
                      {L(item)}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <motion.div {...fadeUp} className="max-w-3xl">
            <p className="mono-tech mb-5 text-[#A8853B]">FAQ</p>
            <h2 className="font-display text-3xl tracking-tight text-[#0E3B33] sm:text-4xl" data-testid="seo-faq-heading">
              {L({ en: "Common questions", it: "Domande frequenti" })}
            </h2>
            <div className="mt-10 space-y-8" data-testid="seo-faq-list">
              {page.faq.map((f, i) => (
                <div key={i} className="border-b border-[#0E3B33]/10 pb-8" data-testid={`seo-faq-item-${i}`}>
                  <h3 className="font-display text-xl text-[#0E3B33]">{L(f.q)}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#4A5248]">{L(f.a)}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* RELATED + CTA BAND */}
      <section className="bg-[#0E3B33] text-[#F1ECDD]">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-end">
            <div className="max-w-xl">
              <p className="mono-tech mb-5 text-[#C9A84C]">{L({ en: "Keep exploring", it: "Continua a esplorare" })}</p>
              <h2 className="font-display text-3xl tracking-tight sm:text-4xl">
                {L({ en: "Tell us what you're making.", it: "Raccontaci cosa stai creando." })}
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-[#F1ECDD]/70">
                {L({
                  en: "Send a sketch, a photo or a CAD file — we reply within 48 hours, in English or Italian.",
                  it: "Invia un bozzetto, una foto o un file CAD — rispondiamo entro 48 ore, in italiano o inglese.",
                })}
              </p>
            </div>
            <Link to="/contact" data-testid="seo-band-cta" className="ember-btn shrink-0">
              {L({ en: "Send my brief →", it: "Invia il mio brief →" })}
            </Link>
          </div>
          <div className="mt-14 flex flex-wrap gap-x-10 gap-y-3 border-t border-white/10 pt-8" data-testid="seo-related-links">
            {page.related.map((r) => (
              <Link key={r} to={`/${r}/`} data-testid={`seo-related-${r}`} className="mono-tech text-[#F1ECDD]/70 transition-colors hover:text-[#C9A84C]">
                {L(SEO_PAGES[r].h1)} →
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
